import { Navigation, Preview } from '@/features/theme-editor';
import { CustomizationMenu } from '@/features/theme-editor/components/CustomizationMenu';
import { LegacyThemeModal } from '@/features/theme-editor/components/LegacyThemeModal';
import { MenuProvider } from '@/features/theme-editor/components/Menu';
import { Loader } from '@/features/theme-editor/components/ui/Loader';
import { PreviewSize } from '@/features/theme-editor/constants/editorThemeConstants';
import { Themes } from '@/features/theme-editor/constants/themeConstants';
import { useAutoScrollOnFocus } from '@/features/theme-editor/hooks/useAutoScrollOnFocus';
import { useBlocks } from '@/features/theme-editor/hooks/useBlocks';
import { useDevice } from '@/features/theme-editor/hooks/useDevice';

import { useCustomizationStore } from '@/features/theme-editor/stores/customizationStore';
import { useThemeEditorStore } from '@/features/theme-editor/stores/themeEditorStore';
import { cn } from '@/lib';
import { configSettings } from '@/lib/config';
import { useMyStore, useUser } from '@/queries';
import { useEffect, useMemo, useState } from 'react';
import { tv } from 'tailwind-variants';

function Editor() {
    const { menuOpen, previewSize: size, editBlockId, errorBlockId } = useThemeEditorStore();
    const { editBlock, addBlock } = useBlocks();
    const { isLoading, theme } = useCustomizationStore();
    const [isPreviewLoaded, setIsPreviewLoaded] = useState(false);
    const [isReady, setIsReady] = useState(false);
    const { isTouch } = useDevice();

    // On Android the keyboard opens on top of menu-inputs, this hook scrolls the input into view when it is focused.
    useAutoScrollOnFocus();

    useEffect(() => {
        if (!isPreviewLoaded) return;

        setTimeout(() => setIsReady(true), 1000);
    }, [isPreviewLoaded]);

    const previewVariants = tv({
        base: 'h-full w-full',
        variants: {
            size: {
                [PreviewSize.DESKTOP]: 'w-full',
                [PreviewSize.MOBILE]: 'max-w-[393px]',
            },
        },
    });
    useEffect(() => {
        // If there is an error block id, set that block in focus.
        if (errorBlockId) editBlock(errorBlockId);
    }, [errorBlockId]);

    const isLegacyTheme = useMemo(() => {
        if (theme === null) return null; // Theme not loaded yet
        return !theme || !Themes.find(t => t.id === theme);
    }, [theme]);

    return (
        <div
            className={cn(
                'grid h-dvh w-full grid-cols-[1fr_1fr] grid-rows-[64px_1fr] overflow-hidden',
                isLoading && 'pointer-events-none'
            )}
        >
            <div className='col-span-full'>
                <Navigation />
            </div>
            <div className='relative col-span-full flex flex-1 flex-row overflow-hidden'>
                <div className='relative w-full'>
                    <div
                        className={cn(
                            'pointer-events-none absolute inset-0 z-50 transition-opacity',
                            isLoading ? 'opacity-100 delay-400' : 'opacity-0'
                        )}
                    >
                        <Loader className='bg-neutral-disabled/50' />
                    </div>
                    <Preview
                        src={`${configSettings.frontDomain}iframe/store`}
                        className={previewVariants({ size })}
                        onLoaded={() => setIsPreviewLoaded(true)}
                        onEditBlock={id => id && editBlock(id)}
                        onAddBlock={(id, order) => id && addBlock(Number(id), order)}
                        editBlockId={editBlockId}
                        errorBlockId={errorBlockId}
                        editMode={menuOpen}
                    />
                </div>
                <div
                    className={cn(
                        'ease-in-out-circ absolute inset-x-0 bottom-0 z-20 w-full flex-1 transition-all duration-250 md:static md:h-full md:duration-500',
                        menuOpen && isReady
                            ? `translate-y-0 md:max-w-[400px]`
                            : 'translate-y-full md:max-w-0 md:translate-y-0'
                    )}
                >
                    <div className='overflow-hidden md:h-full md:min-w-[400px]'>
                        <div className='border-neutral-subtle bg-neutral-white flex h-full max-h-[70dvh] flex-col border-t md:max-h-none md:border-t-0 md:border-l'>
                            <CustomizationMenu />
                        </div>
                    </div>
                </div>
                {!isReady && (
                    <div
                        className={cn(
                            'absolute inset-0 transition-opacity delay-600 duration-200',
                            isPreviewLoaded && 'pointer-events-none opacity-0'
                        )}
                    >
                        <Loader label='Loading your preview' />
                    </div>
                )}

                <span
                    className={cn(
                        'bg-stan-950 fixed right-0 bottom-0 left-0 z-10 rounded-t-full p-2 text-center text-xs font-bold text-white md:hidden',
                        !isTouch && 'hidden'
                    )}
                >
                    Tap a section to edit it
                </span>
            </div>

            {isLegacyTheme && <LegacyThemeModal />}
        </div>
    );
}

export default function EditorPage() {
    const { setStore } = useCustomizationStore(state => state);
    const { data: storeResponse } = useMyStore();
    const { user } = useUser();

    // Populate the store with the data from the API
    useEffect(() => {
        if (!storeResponse || !storeResponse.length || !user) return;

        const store = storeResponse[0];
        if (!store) return;
        const { pages, sections, data } = store;

        setStore({
            storeId: store.store_id,
            pages,
            sections,
            data,
            user,
        });
    }, [storeResponse, user, setStore]);

    return (
        <MenuProvider>
            <Editor />
        </MenuProvider>
    );
}
